import { ChevronDown, X } from 'lucide-react';
import { memo, MutableRefObject, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

import { clsx } from 'clsx';
import { useEventListener, useOnClickOutside } from 'usehooks-ts';

import { ICON_DEFAULT } from '@shared/constants/colors';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { HStack, VStack } from '@shared/ui/Stack';

import { getRanges } from '../../../utils/getRange/getRange';
import { RadioListItem, RadioListItemType } from '../RadioListItem/RadioListItem';
import cls from './SelectWithRange.module.scss';

type CheckboxSelectProps = {
  onChange: (min: string, max: string, filterId: string) => void;
  baseMinValue: string;
  baseMaxValue: string;
  minValue: string;
  maxValue: string;
  className?: string;
  label: string;
  filterId: string;
  activeFilters: Record<string, number[]>;
  widthRadioList?: boolean;
};

export const SelectWithRange = memo(
  ({
    onChange,
    className,
    label,
    maxValue,
    minValue,
    baseMinValue,
    baseMaxValue,
    filterId,
    widthRadioList,
    activeFilters
  }: CheckboxSelectProps) => {
    const wrapRef = useRef() as MutableRefObject<HTMLDivElement>;
    const [open, setOpen] = useState(false);
    const [min, setMin] = useState<string>(minValue);
    const [max, setMax] = useState<string>(maxValue);
    const [radioList, setRadioList] = useState<RadioListItemType[]>(
      getRanges(Number(baseMinValue), Number(baseMaxValue), activeFilters[filterId])
    );

    const toggleSelect = () => {
      setOpen(!open);
    };

    const onChangeMinValue = (value: string) => {
      setMin(value.replace(/[^0-9.,]/g, ''));
      setRadioList(radioList.map((el) => ({ ...el, active: false })));
    };

    const onChangeMaxValue = (value: string) => {
      setMax(value.replace(/[^0-9.,]/g, ''));
      setRadioList(radioList.map((el) => ({ ...el, active: false })));
    };

    const onChangeHandler = useCallback(
      (value: RadioListItemType) => {
        const newValue = radioList.map((el) =>
          el.id === value.id
            ? { ...el, active: true }
            : {
                ...el,
                active: false
              }
        );
        setRadioList(newValue);
      },
      [radioList]
    );

    const closeSelect = () => {
      setOpen(false);
    };

    const resetFilter = (e: SyntheticEvent) => {
      e.stopPropagation();
      setRadioList(radioList.map((el) => ({ ...el, active: false })));
      setMax('');
      setMin('');
      onChange('', '', filterId);
    };

    const sendValues = () => {
      const activeValueRange = radioList.find((el) => el.active);

      if (activeValueRange) {
        onChange(activeValueRange.min.toString(), activeValueRange.max.toString(), filterId);
      } else {
        onChange(min, max, filterId);
      }

      closeSelect();
    };

    const onKeyboardClickHandler = (e: KeyboardEvent) => {
      if (!open) return;

      if (e.key === 'Enter') {
        sendValues();
      }
    };

    useEventListener('keydown', onKeyboardClickHandler);

    useOnClickOutside(wrapRef, closeSelect);

    useEffect(() => {
      setMin(minValue);
      setMax(maxValue);
    }, [minValue, maxValue]);

    useEffect(() => {
      if (activeFilters[filterId]) {
        setRadioList(getRanges(Number(baseMinValue), Number(baseMaxValue), activeFilters[filterId]));
      }
    }, [activeFilters, filterId, baseMinValue, baseMaxValue]);

    useEffect(() => {
      if (!activeFilters[filterId]) {
        setRadioList(radioList.map((el) => ({ ...el, active: false })));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters, filterId]);

    const modes = {
      [cls.open]: open
    };

    return (
      <div
        ref={wrapRef}
        data-testid={'filter-select-range'}
        className={clsx(cls.wrap, className, modes)}
      >
        <div className={cls.inner}>
          <div
            data-testid={'filter-select-range-trigger'}
            onClick={toggleSelect}
            className={cls.currentValue}
          >
            <span>{label}</span>
            <ChevronDown
              size={20}
              className={cls.trigger}
              color={ICON_DEFAULT}
            />
            {min !== '' || max !== '' || radioList.find((el) => el.active) !== undefined ? (
              <button
                data-testid={'filter-select-range-reset'}
                onClick={resetFilter}
                className={cls.removeAll}
              >
                <X
                  color={ICON_DEFAULT}
                  size={18}
                />
              </button>
            ) : null}
          </div>
          <div className={cls.options}>
            <HStack gap={'8'}>
              <div className={cls.field}>
                <Input
                  testId={'filter-select-range-input-min'}
                  size={'s'}
                  label={`от ${baseMinValue}`}
                  className={cls.input}
                  value={min}
                  onChange={onChangeMinValue}
                />
              </div>
              <div className={cls.field}>
                <Input
                  testId={'filter-select-range-input-max'}
                  size={'s'}
                  label={`до ${baseMaxValue}`}
                  value={max}
                  className={cls.input}
                  onChange={onChangeMaxValue}
                />
              </div>
            </HStack>

            {widthRadioList && (
              <VStack className={cls.radioList}>
                {radioList.map((el, index) => (
                  <RadioListItem
                    key={index}
                    onChange={onChangeHandler}
                    label={el.min === Number(baseMinValue) ? `до ${el.max} см` : `от ${el.min} см - до ${el.max} см`}
                    value={el}
                  />
                ))}
              </VStack>
            )}

            <Button
              testId={'filter-select-range-button'}
              fullWidth
              size={'s'}
              className={cls.button}
              onClick={sendValues}
            >
              Готово
            </Button>
          </div>
        </div>
      </div>
    );
  }
);
